import { useRef, useState } from 'react';
import { Toolbar } from 'primereact/toolbar';
import { Avatar } from 'primereact/avatar';
import { Button } from 'primereact/button';
import { MenuItem } from 'primereact/menuitem';
import { TieredMenu } from 'primereact/tieredmenu';
import { useAuth0, User } from '@auth0/auth0-react';
import { resetUser } from '../../redux/reducers/userSlice';
import { useAppDispatch } from '../../redux/hooks';
import { usePostHog } from '@posthog/react';
import inactiveAccountMascot from '../../assets/inactive-account-mascot.svg';
import loginFailedMascot from '../../assets/login-failed-mascot.svg';
import { toggleBar } from '../../speechstream/toggleToolbar';

export type ErrorType = 'teacherDeactivated' | 'studentDeactivated' | 'teacherSsoMismatch' | 'studentSsoMismatch';

interface NoUserInDBProps {
  auth0User: User | undefined;
  errorType: ErrorType;
}

const SUPPORT_EMAIL = 'support4roadmaps@teachtoone.org';

const getSsoProvider = (sub: string | undefined): string => {
  if (!sub) return 'SSO';
  if (sub.startsWith('google-oauth2|')) return 'Google';
  if (sub.startsWith('oauth2|clever|')) return 'Clever';
  if (sub.startsWith('windowslive|') || sub.startsWith('AzureAD|') || sub.startsWith('waad|')) return 'Microsoft';
  return 'SSO';
};

const getAlternativeProviders = (sub: string | undefined): string => {
  if (!sub) return 'Clever or Microsoft';
  if (sub.startsWith('google-oauth2|')) return 'Clever or Microsoft';
  if (sub.startsWith('oauth2|clever|')) return 'Google or Microsoft';
  return 'Google or Clever';
};

const FooterErrors = () => {
  const [loadingSpeechStream, setLoadingSpeechStream] = useState(false);

  const loadToolbar = async () => {
    if (window.speechstream.domControlTools) {
      return toggleBar();
    }
    setLoadingSpeechStream(true);
    try {
      await window.speechstream.Loader.lateLoad();
    } catch (err) {
      console.log(err);
    }
    setLoadingSpeechStream(false);
  };

  return (
    <footer className="tw-flex tw-justify-start tw-gap-4 tw-py-4 tw-px-4 tw-text-sm tw-bg-white">
      <button
        disabled={loadingSpeechStream}
        onClick={loadToolbar}
        className="tw-bg-transparent tw-border-none tw-cursor-pointer tw-text-blue-700 tw-font-semibold tw-p-0 tw-text-sm disabled:tw-opacity-50 hover:tw-text-blue-950"
      >
        Accessibility Tools
      </button>
      <a
        href="https://teachtoone.org/terms-of-use/"
        target="_blank"
        rel="noreferrer"
        className="tw-text-blue-700 tw-font-semibold"
      >
        Terms &amp; Rules
      </a>
    </footer>
  );
};

const NoUserInDB = ({ auth0User, errorType }: NoUserInDBProps) => {
  const { logout } = useAuth0();
  const profileMenu = useRef<TieredMenu>(null);
  const dispatch = useAppDispatch();
  const posthog = usePostHog();

  const handleLogout = () => {
    dispatch(resetUser());
    posthog.reset();
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const profileButtonItems: MenuItem[] = [
    {
      label: 'Logout',
      icon: 'nc_icon-nc_close icon-sm',
      command: handleLogout,
    },
  ];

  const header = (
    <header>
      <Toolbar
        className="w-full nc_header"
        start={<div className="nc_logo" aria-label="Home" />}
        end={
          <>
            <TieredMenu model={profileButtonItems} popup ref={profileMenu} id="profile_menu" />
            <Button
              data-testid="profile_menu"
              label={auth0User?.name}
              icon={<Avatar image={auth0User?.picture} className="mr-2" shape="circle" />}
              rounded
              outlined
              onClick={event => profileMenu?.current?.toggle(event)}
              aria-label="User Profile Menu"
              aria-controls="profile_menu"
              className="ph-no-capture"
            />
          </>
        }
      />
    </header>
  );

  if (errorType === 'teacherDeactivated' || errorType === 'studentDeactivated') {
    return (
      <div className="nc_page tw-w-full min-h-screen tw-flex tw-flex-col">
        {header}
        <main
          role="main"
          className="tw-flex-1 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4 tw-px-4 tw-text-center tw-pb-16"
        >
          <img
            src={inactiveAccountMascot}
            alt="Relaxed mascot wearing sunglasses and sipping a juice box"
            className="tw-w-36 tw-h-36"
          />
          <h1 className="tw-text-2xl tw-font-bold tw-text-neutral-900 tw-m-0">Your account is almost ready!</h1>
          <p className="tw-text-base tw-text-neutral-700 tw-m-0">
            We&apos;re working to set you back up for this new school year
          </p>
          <p className="tw-text-base tw-text-neutral-700 tw-m-0">
            <span className="tw-font-bold">Need help?</span> Email us at{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="tw-text-blue-700">
              {SUPPORT_EMAIL}
            </a>
          </p>
          <Button
            label="Back to login"
            icon="pi pi-arrow-left"
            iconPos="left"
            rounded
            onClick={handleLogout}
            className="tw-mt-2"
          />
        </main>
        <FooterErrors />
      </div>
    );
  }

  // same design since we can't distinguish roles
  const provider = getSsoProvider(auth0User?.sub);
  const alternatives = getAlternativeProviders(auth0User?.sub);
  return (
    <div className="nc_page tw-w-full min-h-screen tw-flex tw-flex-col">
      {header}
      <main
        role="main"
        className="tw-flex-1 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4 tw-px-4 tw-text-center tw-pb-16"
      >
        <img
          src={loginFailedMascot}
          alt="Dismissed mascot character with lightning bolts above its head"
          className="tw-w-36 tw-h-36"
        />
        <h1 className="tw-text-2xl tw-font-bold tw-text-neutral-900 tw-m-0">
          This {provider} account isn&apos;t connected to your Roadmaps
        </h1>
        <p className="tw-text-base tw-text-neutral-700 tw-m-0">
          Try a different account, or sign in with {alternatives}
        </p>
        <Button
          label="Back to login"
          icon="pi pi-arrow-left"
          iconPos="left"
          rounded
          onClick={handleLogout}
          className="tw-mt-2"
        />
      </main>
      <FooterErrors />
    </div>
  );
};

export default NoUserInDB;
